import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../../shared/components/Layout';
import Modal from '../../shared/components/Modal';
import Pagination from '../../shared/components/Pagination';
import { useAuth } from '../auth/AuthContext';
import { extractErrorMessage } from '../../shared/utils/errors';
import {
  FinanceCategory,
  FinanceOperation,
  FinancePlatformSummary,
  PaginatedResponse,
} from '../../shared/types';
import {
  OperationFilters,
  PlatformAccountInfo,
  PlatformAccountRow,
  activatePlatformAccount,
  createPlatformAccount,
  deactivatePlatformAccount,
  getPlatformAccount,
  getPlatformAccountHistory,
  getPlatformSummary,
  listOperations,
} from './api';
import OperationDetailModal from './OperationDetailModal';
import {
  CURRENCY_OPTIONS,
  TYPE_LABELS,
  directionClass,
  directionPrefix,
  formatCryptoAmount,
  formatUsd,
} from './format';

type OpsFilters = Pick<OperationFilters, 'currency' | 'category' | 'date_from' | 'date_to'>;

const CATEGORY_OPTIONS: Array<{ value: FinanceCategory | ''; label: string }> = [
  { value: '', label: 'Any' },
  { value: 'p2p', label: 'P2P fees' },
  { value: 'swap', label: 'Swap fees' },
  { value: 'withdrawal', label: 'Withdrawal fees' },
  { value: 'other', label: 'Other' },
];

/**
 * Renders the money movement of a platform-account ledger entry as
 * "source → destination", derived from the entry's direction, description
 * ("... from user #N") and reference conventions (p2p escrow refs).
 */
const flowLabel = (op: FinanceOperation): { from: string; to: string } => {
  const userMatch = op.description?.match(/user #(\d+)/i);
  const counterparty = userMatch ? `User #${userMatch[1]}` : null;
  const escrowMatch = op.reference_id?.match(/^p2p_trade_escrow_(\d+)/);
  const escrow = escrowMatch ? `P2P escrow (trade #${escrowMatch[1]})` : null;

  if (op.direction === 'in') {
    return { from: counterparty ?? escrow ?? 'External', to: 'Platform account' };
  }
  if (op.direction === 'out') {
    return { from: 'Platform account', to: counterparty ?? escrow ?? 'External' };
  }
  return { from: 'Platform account', to: 'Hold' };
};

const formatDate = (value: string | null | undefined): string =>
  value ? new Date(value).toLocaleString() : '—';

const PlatformBalancePage: React.FC = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('finance.manage_platform_account');

  const [info, setInfo] = useState<PlatformAccountInfo | null>(null);
  const [summary, setSummary] = useState<FinancePlatformSummary | null>(null);
  const [history, setHistory] = useState<PlatformAccountRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [operations, setOperations] = useState<PaginatedResponse<FinanceOperation> | null>(null);
  const [isOpsLoading, setIsOpsLoading] = useState(false);
  const [opsError, setOpsError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<OpsFilters>({});
  const [draftFilters, setDraftFilters] = useState<OpsFilters>({});
  const [selectedOperation, setSelectedOperation] = useState<FinanceOperation | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createEmail, setCreateEmail] = useState('');
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [activateTarget, setActivateTarget] = useState<PlatformAccountRow | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const activeUserId = info?.account?.user_id ?? info?.active_user_id ?? null;

  const loadAccount = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [accountInfo, accountHistory] = await Promise.all([
        getPlatformAccount(),
        getPlatformAccountHistory().catch(() => [] as PlatformAccountRow[]),
      ]);
      setInfo(accountInfo);
      setHistory(accountHistory);

      const platformUserId = accountInfo.account?.user_id ?? accountInfo.active_user_id;
      if (platformUserId) {
        try {
          setSummary(await getPlatformSummary());
        } catch {
          setSummary(null);
        }
      } else {
        setSummary(null);
      }
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load platform account'));
      setInfo(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadOperations = useCallback(
    async (page: number, filtersToUse: OpsFilters) => {
      if (!activeUserId) {
        setOperations(null);
        return;
      }
      setIsOpsLoading(true);
      setOpsError(null);
      try {
        const data = await listOperations({
          ...filtersToUse,
          user_id: activeUserId,
          page,
          per_page: 20,
        });
        setOperations(data);
      } catch (err) {
        setOpsError(extractErrorMessage(err, 'Failed to load operations'));
        setOperations(null);
      } finally {
        setIsOpsLoading(false);
      }
    },
    [activeUserId]
  );

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  useEffect(() => {
    loadOperations(currentPage, filters);
  }, [currentPage, filters, loadOperations]);

  const applyFilters = () => {
    setFilters(draftFilters);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setDraftFilters({});
    setFilters({});
    setCurrentPage(1);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setActionError(null);
    try {
      await createPlatformAccount({
        name: createName.trim(),
        ...(createEmail.trim() ? { email: createEmail.trim() } : {}),
      });
      setCreateOpen(false);
      setCreateName('');
      setCreateEmail('');
      await loadAccount();
    } catch (err) {
      setActionError(extractErrorMessage(err, 'Failed to create platform account'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeactivate = async () => {
    setIsSubmitting(true);
    setActionError(null);
    try {
      await deactivatePlatformAccount();
      setDeactivateOpen(false);
      await loadAccount();
    } catch (err) {
      setActionError(extractErrorMessage(err, 'Failed to deactivate platform account'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleActivate = async () => {
    if (!activateTarget) return;
    setIsSubmitting(true);
    setActionError(null);
    try {
      await activatePlatformAccount(activateTarget.id);
      setActivateTarget(null);
      await loadAccount();
    } catch (err) {
      setActionError(extractErrorMessage(err, 'Failed to activate platform account'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const account = info?.account ?? null;
  const balances = summary?.platform_account?.balances ?? [];
  const flows = summary?.platform_account?.flows;
  const pastAccounts = history.filter((h) => !h.is_active);

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Platform Balance</h1>
            <p className="text-slate-400 mt-1">
              The platform revenue account: collected fees, balances and every ledger movement.
            </p>
          </div>
          {account && canManage && (
            <button
              type="button"
              onClick={() => {
                setActionError(null);
                setDeactivateOpen(true);
              }}
              className="btn btn-danger py-2"
            >
              Deactivate account
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="card flex items-center justify-center h-64">
            <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="card p-6 text-center">
            <p className="text-red-400">{error}</p>
          </div>
        ) : !account ? (
          <div className="card p-12 text-center space-y-4">
            <p className="text-slate-300 text-lg">No active platform account.</p>
            <p className="text-sm text-amber-400">
              Swaps, fee-bearing withdrawals and P2P releases are rejected while no platform
              account is active. Create one, or re-activate a previous account below.
            </p>
            {canManage ? (
              <button
                type="button"
                onClick={() => {
                  setActionError(null);
                  setCreateOpen(true);
                }}
                className="btn btn-primary"
              >
                Create platform account
              </button>
            ) : (
              <p className="text-xs text-slate-500">
                You need the “Manage Platform Account” permission to create one.
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="card p-5">
                <div className="flex items-center justify-between">
                  <p className="text-xs uppercase tracking-wider text-slate-500">Account</p>
                  <span className="badge badge-success">Active</span>
                </div>
                <p className="text-xl font-bold text-white mt-2">{account.name}</p>
                <div className="mt-3 space-y-1 text-sm">
                  <p className="text-slate-400">
                    Ledger user: <span className="font-mono text-slate-200">#{account.user_id}</span>
                  </p>
                  <p className="text-slate-400">
                    Type: <span className="badge badge-info">platform</span>
                  </p>
                  {info?.user?.email && (
                    <p className="text-slate-400 truncate" title={info.user.email}>
                      Email: <span className="text-slate-300">{info.user.email}</span>
                    </p>
                  )}
                  <p className="text-slate-400">
                    Activated: <span className="text-slate-300">{formatDate(account.activated_at)}</span>
                  </p>
                </div>
              </div>

              <div className="card p-5">
                <p className="text-xs uppercase tracking-wider text-slate-500">Balances</p>
                <p className="text-2xl font-bold text-white mt-1">
                  {formatUsd(summary?.platform_account?.total_usd ?? 0)}
                </p>
                <div className="mt-3 space-y-1 text-xs font-mono">
                  {balances.map((b) => (
                    <div key={b.currency} className="flex justify-between gap-2">
                      <span className="text-slate-400">{b.currency}</span>
                      <span className="text-right text-slate-200">
                        {formatCryptoAmount(b.balance)}
                        <span className="text-slate-500"> ({formatUsd(b.usd_value)})</span>
                      </span>
                    </div>
                  ))}
                  {balances.length === 0 && (
                    <p className="text-slate-500">No funds collected yet.</p>
                  )}
                </div>
              </div>

              <div className="card p-5">
                <p className="text-xs uppercase tracking-wider text-slate-500">Flows (all time)</p>
                <div className="mt-2 space-y-0.5 text-sm font-mono">
                  <p className="text-emerald-400">in: {formatUsd(flows?.usd_in ?? 0)}</p>
                  <p className="text-rose-400">out: {formatUsd(flows?.usd_out ?? 0)}</p>
                </div>
                <div className="mt-3 space-y-1 text-xs font-mono">
                  {(flows?.currencies ?? []).map((f) => (
                    <div key={f.currency} className="flex justify-between gap-2">
                      <span className="text-slate-400">{f.currency}</span>
                      <span className="text-right">
                        <span className="text-emerald-400">+{formatCryptoAmount(f.in)}</span>
                        <span className="text-slate-600"> / </span>
                        <span className="text-rose-400">−{formatCryptoAmount(f.out)}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="card p-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">Currency</label>
                <select
                  value={draftFilters.currency ?? ''}
                  onChange={(e) =>
                    setDraftFilters((p) => ({ ...p, currency: e.target.value || undefined }))
                  }
                  className="input py-2"
                >
                  {CURRENCY_OPTIONS.map((c) => (
                    <option key={c || 'all-cur'} value={c}>
                      {c || 'Any'}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">Source</label>
                <select
                  value={draftFilters.category ?? ''}
                  onChange={(e) =>
                    setDraftFilters((p) => ({
                      ...p,
                      category: (e.target.value || undefined) as FinanceCategory | undefined,
                    }))
                  }
                  className="input py-2"
                >
                  {CATEGORY_OPTIONS.map((c) => (
                    <option key={c.value || 'all-cat'} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">From</label>
                <input
                  type="date"
                  value={draftFilters.date_from ?? ''}
                  onChange={(e) =>
                    setDraftFilters((p) => ({ ...p, date_from: e.target.value || undefined }))
                  }
                  className="input py-2"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">To</label>
                <input
                  type="date"
                  value={draftFilters.date_to ?? ''}
                  onChange={(e) =>
                    setDraftFilters((p) => ({ ...p, date_to: e.target.value || undefined }))
                  }
                  className="input py-2"
                />
              </div>
              <div className="flex gap-2 lg:col-span-2">
                <button type="button" onClick={applyFilters} className="btn btn-primary py-2 flex-1">
                  Apply
                </button>
                <button type="button" onClick={clearFilters} className="btn btn-secondary py-2">
                  Clear
                </button>
              </div>
            </div>

            <div className="card overflow-hidden">
              {isOpsLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
                </div>
              ) : opsError ? (
                <div className="p-6 text-center">
                  <p className="text-red-400">{opsError}</p>
                </div>
              ) : !operations?.data.length ? (
                <div className="p-12 text-center text-slate-400">No operations found.</div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-800/50">
                        <tr>
                          <th className="table-header">Date</th>
                          <th className="table-header">Operation</th>
                          <th className="table-header">Movement</th>
                          <th className="table-header text-right">Amount</th>
                          <th className="table-header text-right">Balance after</th>
                          <th className="table-header">Details</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800">
                        {operations.data.map((op) => {
                          const flow = flowLabel(op);
                          return (
                            <tr
                              key={op.id}
                              className="hover:bg-slate-800/30 transition-colors cursor-pointer"
                              onClick={() => setSelectedOperation(op)}
                            >
                              <td className="table-cell text-slate-400 text-sm whitespace-nowrap">
                                {new Date(op.created_at).toLocaleString()}
                                <p className="text-xs text-slate-600 font-mono">#{op.id}</p>
                              </td>
                              <td className="table-cell text-slate-300 text-sm whitespace-nowrap">
                                {TYPE_LABELS[op.type] ?? op.type}
                                <p className="text-xs text-slate-500">{op.category}</p>
                              </td>
                              <td className="table-cell text-sm whitespace-nowrap">
                                <span className="text-slate-300">{flow.from}</span>
                                <span className="text-slate-600"> → </span>
                                <span className="text-slate-300">{flow.to}</span>
                              </td>
                              <td
                                className={`table-cell text-right font-mono whitespace-nowrap ${directionClass(op.direction)}`}
                              >
                                {directionPrefix(op.direction)}
                                {formatCryptoAmount(op.amount)}{' '}
                                <span className="text-slate-500">{op.currency}</span>
                              </td>
                              <td className="table-cell text-right font-mono text-slate-400 whitespace-nowrap">
                                {formatCryptoAmount(op.balance_after)}
                              </td>
                              <td className="table-cell max-w-[280px]">
                                <p
                                  className="text-sm text-slate-300 truncate"
                                  title={op.description ?? ''}
                                >
                                  {op.description ?? '—'}
                                </p>
                                {op.reference_id && (
                                  <p
                                    className="text-xs text-slate-600 font-mono truncate"
                                    title={op.reference_id}
                                  >
                                    {op.reference_id}
                                  </p>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {operations.last_page > 1 && (
                    <div className="p-4 border-t border-slate-800 flex items-center justify-between gap-4">
                      <p className="text-xs text-slate-500">
                        {operations.total.toLocaleString('en-US')} operation
                        {operations.total === 1 ? '' : 's'}
                      </p>
                      <Pagination
                        currentPage={operations.current_page}
                        lastPage={operations.last_page}
                        onPageChange={setCurrentPage}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {pastAccounts.length > 0 && (
          <div className="card overflow-hidden">
            <div className="p-4 border-b border-slate-800">
              <h2 className="text-sm font-semibold text-white">Previous platform accounts</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="table-header">Name</th>
                    <th className="table-header">Ledger user</th>
                    <th className="table-header">Activated</th>
                    <th className="table-header">Deactivated</th>
                    {canManage && <th className="table-header text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {pastAccounts.map((h) => (
                    <tr key={h.id}>
                      <td className="table-cell text-slate-300">{h.name}</td>
                      <td className="table-cell font-mono text-slate-400">#{h.user_id}</td>
                      <td className="table-cell text-slate-400 text-sm">{formatDate(h.activated_at)}</td>
                      <td className="table-cell text-slate-400 text-sm">{formatDate(h.deactivated_at)}</td>
                      {canManage && (
                        <td className="table-cell text-right">
                          <button
                            type="button"
                            onClick={() => {
                              setActionError(null);
                              setActivateTarget(h);
                            }}
                            className="btn btn-secondary py-1.5 px-3 text-sm"
                            disabled={!!account}
                            title={account ? 'Deactivate the current account first' : undefined}
                          >
                            Activate
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create platform account"
        size="md"
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <p className="text-sm text-slate-400">
            Creates a dedicated service user of type “platform” that collects all platform fees.
            It cannot sign in and only one platform account can be active at a time.
          </p>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Name *</label>
            <input
              className="input"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              placeholder="e.g. Platform Revenue"
              required
              maxLength={255}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Email (optional, generated when empty)
            </label>
            <input
              className="input"
              type="email"
              value={createEmail}
              onChange={(e) => setCreateEmail(e.target.value)}
              placeholder="platform@company.internal"
              maxLength={255}
            />
          </div>
          {actionError && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {actionError}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setCreateOpen(false)}
              className="btn btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={deactivateOpen}
        onClose={() => setDeactivateOpen(false)}
        title="Deactivate platform account"
        size="md"
      >
        <div className="space-y-4">
          <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-sm">
            Warning: after deactivation the platform has no active fee account. Swaps and
            fee-bearing withdrawals will be rejected and P2P releases will fail until an account
            is activated or created again.
          </div>
          <p className="text-sm text-slate-300">
            Existing balances and history stay in the ledger untouched. You can re-activate this
            account later from the “Previous platform accounts” list.
          </p>
          {actionError && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {actionError}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setDeactivateOpen(false)}
              className="btn btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDeactivate}
              className="btn btn-danger"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Deactivating…' : 'Deactivate'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={activateTarget !== null}
        onClose={() => setActivateTarget(null)}
        title="Activate platform account"
        size="md"
      >
        <div className="space-y-4">
          <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-sm">
            Warning: only one platform account can be active at a time. From the moment of
            activation, all platform fees (P2P, swap, withdrawal) will be booked to this account.
          </div>
          {activateTarget && (
            <p className="text-sm text-slate-300">
              Activate <span className="font-semibold text-white">{activateTarget.name}</span>{' '}
              (ledger user <span className="font-mono">#{activateTarget.user_id}</span>)? Its
              previous balances remain on the account and stay part of the platform balance.
            </p>
          )}
          {actionError && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {actionError}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setActivateTarget(null)}
              className="btn btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleActivate}
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Activating…' : 'Activate'}
            </button>
          </div>
        </div>
      </Modal>

      <OperationDetailModal
        isOpen={selectedOperation !== null}
        onClose={() => setSelectedOperation(null)}
        operation={selectedOperation}
        userLabel={selectedOperation ? `Platform account #${selectedOperation.user_id}` : ''}
        showCategory
      />
    </Layout>
  );
};

export default PlatformBalancePage;
